import {ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, SimpleChanges} from "@angular/core";
// noinspection TypeScriptPreferShortImport
import {NFrameworkIconsFactory} from "../../services/nframework-icons-factory.service";
import {INIconSet} from "../../";

const awaiters: { [iconSet: string]: Array<(set: INIconSet) => void> } = {};
const fetchingSets: { [iconSet: string]: boolean } = {};
const loadedSets: { [iconSet: string]: INIconSet } = {};

export const BUILTIN_ICON_SET = "builtin";

@Component({
  selector: "n-icon",
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: ``,
})
export class NIconComponent implements OnChanges {

  @Input() iconId: string;
  @Input() iconSize: any;
  @Input() optionalSize: boolean;
  @Input() iconSet: string;
  @Input() autoScale: boolean;

  @Input() fillColor: string;
  @Input() strokeColor: string;
  @Input() borderColor: string;

  private svg: SVGElement;
  private borderPath: SVGPathElement;
  private fillPath: SVGPathElement;

  constructor(private iconsFactory: NFrameworkIconsFactory,
              private el: ElementRef) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    const {iconSet, iconId, fillColor, strokeColor, borderColor} = changes;
    if (iconId || iconSet || fillColor || strokeColor || borderColor) {
      this.refresh();
    }
  }

  private async refresh() {
    const iconSet = await this.ensureIconSet(this.iconSet);

    const icon = iconSet[this.iconId];
    if (!icon) {
      const pathInfo = getPathTo(this.el.nativeElement);
      throw new Error(`Icon with id="${this.iconId}" not found in icon set "${this.iconSet || "default"}. Path: ${pathInfo}"`);
    }

    let specificIcon = this.iconSize ? icon.find(i => i.size === this.iconSize.toString()) : icon[0];

    if (!specificIcon) {
      if (this.optionalSize) {
        specificIcon = icon[0];
      } else {
        throw new Error(`Icon '${this.iconId}' does not contain size '${this.iconSize}'`);
      }
    }

    const container = this.el.nativeElement.shadowRoot || this.el.nativeElement;
    const viewBoxSize = specificIcon.size || this.iconSize;
    if (!this.svg) {
      this.svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      this.svg.style.verticalAlign = "top";
      this.svg.style.overflow = "hidden";
      this.svg.setAttribute("role", "img");
      this.svg.setAttribute("aria-label", this.iconId);
      container.appendChild(this.svg);
    }

    if (viewBoxSize) {
      this.svg.setAttribute("viewBox", `0 0 ${viewBoxSize} ${viewBoxSize}`);
    } else {
      this.svg.removeAttribute("viewBox");
    }

    const useIconSize = this.iconSize && !this.autoScale;
    this.svg.style.width = useIconSize ? this.iconSize + "px" : "100%";
    this.svg.style.height = useIconSize ? this.iconSize + "px" : "100%";

    if (specificIcon.border) {
      if (!this.borderPath) {
        this.borderPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
        this.borderPath.setAttribute("fill-rule", "evenodd");
        this.borderPath.setAttribute("class", "n-icon__border");
        this.svg.appendChild(this.borderPath);
      }
      this.borderPath.setAttribute("d", specificIcon.border);
      if (this.borderColor) {
        this.borderPath.setAttribute("style", "fill: " + this.borderColor);
      } else {
        this.borderPath.removeAttribute("style");
      }
    } else if (this.borderPath) {
      this.svg.removeChild(this.borderPath);
      this.borderPath = undefined;
    }

    if (!this.fillPath) {
      this.fillPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
      this.fillPath.setAttribute("class", "n-icon__fill");
      this.fillPath.setAttribute("fill-rule", "evenodd");
      this.svg.appendChild(this.fillPath);
    }

    this.fillPath.setAttribute("d", specificIcon.fill);
    this.fillPath.style.fill = this.fillColor;
    this.fillPath.style.stroke = this.strokeColor;

  }

  private ensureIconSet(iconSet?: string): Promise<INIconSet> {

    if (this.iconSet === BUILTIN_ICON_SET) {
      return Promise.resolve(builtInIcons);
    }

    if (loadedSets[iconSet]) {
      return Promise.resolve(loadedSets[iconSet]);
    }

    if (fetchingSets[iconSet]) {
      if (!awaiters[iconSet]) {
        awaiters[iconSet] = [];
      }
      return new Promise(resolve => awaiters[iconSet].push(resolve));
    }

    fetchingSets[iconSet] = true;

    return this.iconsFactory.getIconSet(this.iconSet).then(set => {

      fetchingSets[iconSet] = false;
      if (awaiters[iconSet]) {
        for (const awaiter of awaiters[iconSet]) {
          awaiter(set);
        }
      }
      awaiters[iconSet] = [];
      return loadedSets[iconSet] = set;
    });
  }

}

const builtInIcons: INIconSet = {
  "calendar": [
    {
      size: "14",
      fill: "M1 1h3V.5a.5.5 0 0 1 1 0V1h4V.5a.5.5 0 0 1 1 0V1h3v11a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V1zm1 1v2h10V2h-2v.5a.5.5 0 0 1-1 0V2H5v.5a.5.5 0 0 1-1 0V2H2zm1 11h8a1 1 0 0 0 1-1V5H2v7a1 1 0 0 0 1 1zm6.5-7h1a.5.5 0 1 1 0 1h-1a.5.5 0 0 1 0-1zm-3 2h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1 0-1zm-3 0h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1 0-1zm3-2h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1 0-1zm3 2h1a.5.5 0 1 1 0 1h-1a.5.5 0 0 1 0-1zm-6 2h1a.5.5 0 1 1 0 1h-1a.5.5 0 1 1 0-1zm3 0h1a.5.5 0 1 1 0 1h-1a.5.5 0 1 1 0-1z"
    }
  ],
  "back": [
    {
      size: "14",
      fill: "M4.207,7l5.596,5.596l-0.707,0.707l-6.303,-6.303l6.303,-6.303l0.707,0.707l-5.596,5.596Z"
    }
  ],
  "next": [
    {
      size: "14",
      fill: "M9.792,7l-5.596,5.596l0.707,0.707l6.303,-6.303l-6.303,-6.303l-0.707,0.707l5.596,5.596Z"
    }
  ]
};

function getPathTo(element) {
  if (element.id !== "") {
    return "id(\"" + element.id + "\")";
  }
  if (element === document.body) {
    return element.tagName;
  }

  let ix = 0;
  const siblings = element.parentNode.childNodes;
  for (let i = 0; i < siblings.length; i++) {
    const sibling = siblings[i];
    if (sibling === element) {
      return getPathTo(element.parentNode) + "/" + element.tagName.toLowerCase()
        + (element.className ? `[@className=${element.className}]` : "")
        + "[" + (ix + 1) + "]";
    }
    if (sibling.nodeType === 1 && sibling.tagName === element.tagName) {
      ix++;
    }
  }
}
